import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save } from "lucide-react";

export default function AdminHomepage() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/public/homepage").then((r) => setForm(r.data || {})); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      ["stats_students", "stats_volunteers", "stats_donations", "stats_programs"].forEach((k) => {
        if (payload[k] !== undefined && payload[k] !== "") payload[k] = Number(payload[k]);
      });
      await api.put("/admin/homepage", payload);
      toast.success("Homepage updated");
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  if (!form) return <DashboardLayout><div className="py-10 text-slate-500">Loading...</div></DashboardLayout>;

  const upd = (k, v) => setForm({ ...form, [k]: v });

  return (
    <DashboardLayout>
      <h1 className="heading text-2xl font-bold">Homepage CMS</h1>
      <p className="text-sm text-slate-500 mt-1">Edit everything shown on the public homepage.</p>
      <form onSubmit={save} className="mt-6 grid lg:grid-cols-2 gap-5">
        <Section title="Founder / Director">
          <Inp label="Founder Name" v={form.founder_name} on={(v) => upd("founder_name", v)} test="cms-founder-name" />
          <Inp label="Designation" v={form.founder_designation} on={(v) => upd("founder_designation", v)} test="cms-founder-designation" />
          <Area label="Founder's Message" v={form.founder_message} on={(v) => upd("founder_message", v)} rows={6} test="cms-founder-message" />
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Founder Photo</label>
            <div className="mt-2 flex items-center gap-4">
              {form.founder_photo_url && (
                <img src={form.founder_photo_url.startsWith("http") || form.founder_photo_url.startsWith("/") ? form.founder_photo_url : form.founder_photo_url} alt="Founder" className="w-20 h-24 object-cover rounded-lg border border-slate-200" />
              )}
              <div className="flex-1">
                <input
                  value={form.founder_photo_url || ""}
                  onChange={(e) => upd("founder_photo_url", e.target.value)}
                  placeholder="Image URL (or use upload below)"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  data-testid="cms-founder-photo-url"
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const fd = new FormData();
                    fd.append("file", file);
                    try {
                      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      upd("founder_photo_url", data.url);
                      toast.success("Founder photo uploaded");
                    } catch { toast.error("Upload failed"); }
                  }}
                  className="mt-2 text-xs"
                  data-testid="cms-founder-photo-upload"
                />
              </div>
            </div>
          </div>
        </Section>

        <Section title="Co-Founder / Second Founder">
          <Inp label="Name" v={form.founder2_name || ""} on={(v) => upd("founder2_name", v)} test="cms-founder2-name" />
          <Inp label="Designation" v={form.founder2_designation || ""} on={(v) => upd("founder2_designation", v)} test="cms-founder2-designation" />
          <Area label="Message" v={form.founder2_message || ""} on={(v) => upd("founder2_message", v)} rows={6} test="cms-founder2-message" />
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Photo</label>
            <div className="mt-2 flex items-center gap-4">
              {form.founder2_photo_url && (
                <img src={form.founder2_photo_url} alt="Co-Founder" className="w-20 h-24 object-cover rounded-lg border border-slate-200" />
              )}
              <div className="flex-1">
                <input
                  value={form.founder2_photo_url || ""}
                  onChange={(e) => upd("founder2_photo_url", e.target.value)}
                  placeholder="Image URL (or use upload below)"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  data-testid="cms-founder2-photo-url"
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const fd = new FormData();
                    fd.append("file", file);
                    try {
                      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      upd("founder2_photo_url", data.url);
                      toast.success("Co-founder photo uploaded");
                    } catch { toast.error("Upload failed"); }
                  }}
                  className="mt-2 text-xs"
                  data-testid="cms-founder2-photo-upload"
                />
              </div>
            </div>
          </div>
        </Section>
        <Section title="Certificate Assets (Signature & Stamp)">
          <p className="text-xs text-slate-500 mb-2">Upload PNG (transparent background recommended). These appear above the MIMANSHA KUMARI signature line on every issued certificate.</p>
          {[
            { key: "certificate_signature_url", label: "Founder Signature (PNG)", testid: "cms-cert-signature" },
            { key: "certificate_stamp_url", label: "Official Round Stamp (PNG)", testid: "cms-cert-stamp" },
          ].map((cfg) => (
            <div key={cfg.key} className="mb-2">
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{cfg.label}</label>
              <div className="mt-2 flex items-center gap-4">
                {form[cfg.key] && (
                  <img src={form[cfg.key]} alt={cfg.label} className="w-24 h-16 object-contain rounded-lg border border-slate-200 bg-slate-50 p-1" />
                )}
                <div className="flex-1">
                  <input
                    value={form[cfg.key] || ""}
                    onChange={(e) => upd(cfg.key, e.target.value)}
                    placeholder="Image URL (or upload below)"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                    data-testid={`${cfg.testid}-url`}
                  />
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const fd = new FormData();
                      fd.append("file", file);
                      try {
                        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                        upd(cfg.key, data.url);
                        toast.success(`${cfg.label} uploaded`);
                      } catch { toast.error("Upload failed"); }
                    }}
                    className="mt-2 text-xs"
                    data-testid={`${cfg.testid}-upload`}
                  />
                </div>
              </div>
            </div>
          ))}
        </Section>

        <Section title="Hero">
          <Inp label="Title" v={form.hero_title} on={(v) => upd("hero_title", v)} test="cms-hero-title" />
          <Area label="Subtitle" v={form.hero_subtitle} on={(v) => upd("hero_subtitle", v)} test="cms-hero-sub" />
          <Inp label="Video URL (YouTube embed)" v={form.video_url || ""} on={(v) => upd("video_url", v)} test="cms-video-url" />
        </Section>
        <Section title="About">
          <Area label="About Text" v={form.about_text} on={(v) => upd("about_text", v)} rows={6} test="cms-about" />
        </Section>
        <Section title="Impact Stats">
          <div className="grid grid-cols-2 gap-3">
            <Inp label="Students" type="number" v={form.stats_students} on={(v) => upd("stats_students", v)} test="cms-stats-students" />
            <Inp label="Volunteers" type="number" v={form.stats_volunteers} on={(v) => upd("stats_volunteers", v)} test="cms-stats-vol" />
            <Inp label="Donations (₹)" type="number" v={form.stats_donations} on={(v) => upd("stats_donations", v)} test="cms-stats-donations" />
            <Inp label="Programs" type="number" v={form.stats_programs} on={(v) => upd("stats_programs", v)} test="cms-stats-programs" />
          </div>
        </Section>
        <Section title="Contact & WhatsApp">
          <Inp label="WhatsApp Number (e.g. 919999999999)" v={form.whatsapp_number} on={(v) => upd("whatsapp_number", v)} test="cms-wa-number" />
          <Inp label="Contact Email" v={form.contact_email} on={(v) => upd("contact_email", v)} test="cms-contact-email" />
          <Inp label="Contact Phone" v={form.contact_phone} on={(v) => upd("contact_phone", v)} test="cms-contact-phone" />
          <Inp label="Address" v={form.contact_address} on={(v) => upd("contact_address", v)} test="cms-contact-address" />
        </Section>
        <div className="lg:col-span-2">
          <button type="submit" disabled={saving} className="bg-brand hover:bg-[#172b66] disabled:opacity-60 text-white font-semibold rounded-lg px-6 py-3 inline-flex items-center gap-2" data-testid="cms-save-button">
            <Save size={16} /> {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </DashboardLayout>
  );
}

function Section({ title, children }) {
  return (
    <div className="p-5 rounded-xl border border-slate-200 bg-white">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Inp({ label, v, on, type = "text", test }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</label>
      <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 outline-none focus:border-brand" data-testid={test} />
    </div>
  );
}
function Area({ label, v, on, rows = 3, test }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</label>
      <textarea rows={rows} value={v || ""} onChange={(e) => on(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 outline-none focus:border-brand resize-none" data-testid={test} />
    </div>
  );
}
